import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Switch,
  FormLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Add,
  Visibility,
  Search,
  FilterList,
  Refresh,
  AttachMoney,
  Phone,
  WhatsApp,
  Email,
  LocationOn,
  AccountBalance,
  MoreVert,
  Notifications
} from '@mui/icons-material';
import { Customer, Loan, BorrowerAPIItem } from '../../models/LendingModels';
import { mockLoans } from '../../data/MockData';
import { formatCurrency } from '../../utils/CurrencyFormat';
import { getBorrowers, createBorrowers, sendBalanceNotification } from '../../services/request/Borrowers';
import { getDebitsByBorrowers, createDebits } from '../../services/request/Debits';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getAllPayments } from '../../services/request/Payments';
import DebitCaptureModal from '../admin/outstandings/DebitCaptureChild';
import AmountInput from '../../shared-components/amount-input/AmountInput';
import BrochureViewer from '../admin/brochure/BrochureViewer';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CustomerManagement: React.FC = () => {
  const navigate = useNavigate();
  const [apiBorrowers, setApiBorrowers] = useState<BorrowerAPIItem[]>([]);
  const [loans] = useState<Loan[]>(mockLoans);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [mainTabValue, setMainTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openAddDebitDialog, setOpenAddDebitDialog] = useState(false);
  const [openCaptureDebitDialog, setOpenCaptureDebitDialog] = useState(false);
  const [selectedBorrowerId, setSelectedBorrowerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [newBorrower, setNewBorrower] = useState({ 
    name: '', 
    comments: '',
    contactNumber: '',
    whatsappNumber: '',
    isContactSameAsWhatsapp: false,
    email: '',
    address: '',
    pincode: '',
    city: '',
    state: '',
    referralCode: '',
    creditScore: '',
  });
  const [debits, setDebits] = useState<any[]>([]);
  const [loadingDebits, setLoadingDebits] = useState(false);
  const [allPayments, setAllPayments] = useState<any[]>([]);
  const [loadingAllPayments, setLoadingAllPayments] = useState(false);
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);
  const [newDebit, setNewDebit] = useState({
    amount: '',
    outstanding: '',
    interestRate: '',
    durationType: '',
    startDate: '',
    endDate: '',
    paymentDate: '',
    emiAmount: '',
    emiDate: '',
    status: 'ACTIVE'
  });
  const [customerPage, setCustomerPage] = useState(0);
  const [customerRowsPerPage, setCustomerRowsPerPage] = useState(10);
  const [totalBorrowers, setTotalBorrowers] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBorrowerForMenu, setSelectedBorrowerForMenu] = useState<BorrowerAPIItem | null>(null);
  const [openNotifyDialog, setOpenNotifyDialog] = useState(false);
  const [notificationData, setNotificationData] = useState({
    useSystemContact: true,
    email: '',
    contactNumber: '',
    sendEmail: true,
    sendSMS: false,
  });
  const [openBrochureDialog, setOpenBrochureDialog] = useState(false);
  const [selectedBorrowerForBrochure, setSelectedBorrowerForBrochure] = useState<BorrowerAPIItem | null>(null);

  // Fetch borrowers from API: http://localhost:1996/api/v1/borrowers?includeOutstanding=true
  const fetchBorrowers = useCallback(async (page = customerPage, pageSize = customerRowsPerPage) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBorrowers(page, pageSize, {});
      console.log("Response ", response);
      if (response.code === 200) {
        setApiBorrowers(response.result);
        const totalCount = response.totalCount ?? response.result?.length ?? 0;
        setTotalBorrowers(totalCount);
        setApiMessage(`Successfully loaded ${totalCount} borrowers`);
      } else {
        setTotalBorrowers(0);
        setError(`API Error: ${response.message}`);
      }
    } catch (err) {
      setTotalBorrowers(0);
      setError(`Failed to fetch borrowers: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Error fetching borrowers:', err);
    } finally {
      setLoading(false);
    }
  }, [customerPage, customerRowsPerPage]);

  useEffect(() => {
    fetchBorrowers();
  }, [fetchBorrowers]);

  useEffect(() => {
    setCustomerPage(0);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (mainTabValue === 1) {
      fetchAllPayments();
    }
  }, [mainTabValue, paymentPage, paymentRowsPerPage]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewCustomer = async (borrower: BorrowerAPIItem) => {
    // Navigate to customer details page
    navigate(`/admin/customers/${borrower.id}`);
  };

  const handleViewDebits = (borrower: BorrowerAPIItem) => {
    // Navigate to customer debits page
    navigate(`/admin/customers/${borrower.id}/debits`);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCustomer(null);
  };

  const handleOpenAddDialog = () => {
    setNewBorrower({ 
      name: '', 
      comments: '',
      contactNumber: '',
      whatsappNumber: '',
      isContactSameAsWhatsapp: false,
      email: '',
      address: '',
      pincode: '',
      city: '',
      state: '',
      referralCode: '',
      creditScore: '',
    });
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewBorrower({ 
      name: '', 
      comments: '',
      contactNumber: '',
      whatsappNumber: '',
      isContactSameAsWhatsapp: false,
      email: '',
      address: '',
      pincode: '',
      city: '',
      state: '',
      referralCode: '',
      creditScore: '',
    });
  };

  const handleAddBorrower = async () => {
    // Validate form
    if (!newBorrower.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await createBorrowers({
        name: newBorrower.name,
        comments: newBorrower.comments,
        contactNumber: newBorrower.contactNumber || undefined,
        whatsappNumber: newBorrower.whatsappNumber || undefined,
        isContactSameAsWhatsapp: newBorrower.isContactSameAsWhatsapp,
        email: newBorrower.email || undefined,
        address: newBorrower.address || undefined,
        pincode: newBorrower.pincode || undefined,
        city: newBorrower.city || undefined,
        state: newBorrower.state || undefined,
        referralCode: newBorrower.referralCode || undefined,
        creditScore: newBorrower.creditScore ? parseInt(newBorrower.creditScore) : undefined,
      });
      
      console.log('Create borrower response:', response);
      
      if (response && (response.code === 200 || response.code === 201)) {
        setApiMessage('Borrower added successfully!');
        handleCloseAddDialog();
        // Refresh the list
        await fetchBorrowers();
      } else {
        setError(`Failed to add borrower: ${response?.message || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Failed to add borrower: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Error adding borrower:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch debits for a customer
  const fetchDebits = async (borrowerId: string) => {
    try {
      setLoadingDebits(true);
      const response = await getDebitsByBorrowers(borrowerId, 0, 10, {});
      console.log('Debits response:', response);
      
      if (response.data && response.data.code === 200) {
        setDebits(response.data.result || []);
      } else {
        setDebits([]);
      }
    } catch (err) {
      console.error('Error fetching debits:', err);
      setDebits([]);
    } finally {
      setLoadingDebits(false);
    }
  };

  const handleOpenAddDebitDialog = () => {
    setNewDebit({
      amount: '',
      outstanding: '',
      interestRate: '',
      durationType: 'MONTHLY',
      startDate: '',
      endDate: '',
      paymentDate: '',
      emiAmount: '',
      emiDate: '',
      status: 'ACTIVE'
    });
    setOpenAddDebitDialog(true);
  };

  const handleCloseAddDebitDialog = () => {
    setOpenAddDebitDialog(false);
    setNewDebit({
      amount: '',
      outstanding: '',
      interestRate: '',
      durationType: 'MONTHLY',
      startDate: '',
      endDate: '',
      paymentDate: '',
      emiAmount: '',
      emiDate: '',
      status: 'ACTIVE'
    });
  };

  const handleAddDebit = async () => {
    if (!selectedCustomer) return;

    // Validate required fields
    if (!newDebit.amount || !newDebit.outstanding || !newDebit.startDate) {
      setError('Amount, Outstanding, and Start Date are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const debitData = {
        amount: parseFloat(newDebit.amount),
        outstanding: parseFloat(newDebit.outstanding),
        interestRate: parseFloat(newDebit.interestRate) || 0,
        durationType: newDebit.durationType,
        startDate: newDebit.startDate,
        endDate: newDebit.endDate,
        paymentDate: parseInt(newDebit.paymentDate) || 1,
        emiAmount: parseFloat(newDebit.emiAmount) || 0,
        emiDate: newDebit.emiDate,
        status: newDebit.status
      };

      const response = await createDebits(selectedCustomer.id, debitData);
      console.log('Create debit response:', response);
      
      if (response && response.data && (response.data.code === 200 || response.data.code === 201)) {
        setApiMessage('Debit added successfully!');
        handleCloseAddDebitDialog();
        // Refresh debits list
        await fetchDebits(selectedCustomer.id);
      } else if (response && (response.code === 200 || response.code === 201)) {
        setApiMessage('Debit added successfully!');
        handleCloseAddDebitDialog();
        // Refresh debits list
        await fetchDebits(selectedCustomer.id);
      } else {
        setError(`Failed to add debit: ${response?.data?.message || response?.message || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Failed to add debit: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Error adding debit:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPayments = async () => {
    try {
      setLoadingAllPayments(true);
      const response = await getAllPayments(paymentPage, paymentRowsPerPage);
      
      if (response.data && response.data.code === 200) {
        setAllPayments(response.data.result || []);
        setTotalPayments(response.data.totalCount || 0);
      } else if (response.status === 200 && response.data) {
        setAllPayments(response.data.result || []);
        setTotalPayments(response.data.totalCount || 0);
      }
    } catch (err) {
      console.error('Error fetching all payments:', err);
      setError('Failed to fetch payment history');
    } finally {
      setLoadingAllPayments(false);
    }
  };

  const handleMainTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setMainTabValue(newValue);
  };

  const handleCustomerPageChange = (_event: unknown, newPage: number) => {
    setCustomerPage(newPage);
  };

  const handleCustomerRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomerRowsPerPage(Number.parseInt(event.target.value, 10));
    setCustomerPage(0);
  };

  const handlePaymentPageChange = (event: unknown, newPage: number) => {
    setPaymentPage(newPage);
  };

  const handlePaymentRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentRowsPerPage(Number.parseInt(event.target.value, 10));
    setPaymentPage(0);
  };

  const handleOpenCaptureDebitDialog = (borrowerId: string) => {
    setSelectedBorrowerId(borrowerId);
    setOpenCaptureDebitDialog(true);
  };

  const handleCloseCaptureDebitDialog = () => {
    setOpenCaptureDebitDialog(false);
    setSelectedBorrowerId(null);
  };

  const handleDebitCreated = () => {
    // Refresh the borrowers list after debit is created
    fetchBorrowers();
    setApiMessage('Debit captured successfully!');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, borrower: BorrowerAPIItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedBorrowerForMenu(borrower);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedBorrowerForMenu here - it might be needed for dialogs
  };

  const handleNotifyCustomer = () => {
    console.log('handleNotifyCustomer called, selectedBorrowerForMenu:', selectedBorrowerForMenu);
    if (selectedBorrowerForMenu) {
      setNotificationData({
        useSystemContact: true,
        email: selectedBorrowerForMenu.email || '',
        contactNumber: selectedBorrowerForMenu.contactNumber || '',
        sendEmail: true,
        sendSMS: false,
      });
      setOpenNotifyDialog(true);
    }
    // Close the menu
    setAnchorEl(null);
  };

  const handleCloseNotifyDialog = () => {
    setOpenNotifyDialog(false);
    setNotificationData({
      useSystemContact: true,
      email: '',
      contactNumber: '',
      sendEmail: true,
      sendSMS: false,
    });
    setSelectedBorrowerForMenu(null);
  };

  const handleSendNotification = async () => {
    console.log('handleSendNotification called');
    console.log('selectedBorrowerForMenu:', selectedBorrowerForMenu);
    console.log('notificationData:', notificationData);
    
    if (!selectedBorrowerForMenu) {
      console.error('No borrower selected');
      setError('No borrower selected');
      return;
    }

    if (!notificationData.sendEmail && !notificationData.sendSMS) {
      setError('Please select at least one notification method (Email or SMS)');
      return;
    }

    // Only validate manually entered fields, not system contact
    if (!notificationData.useSystemContact) {
      // Validate manually entered email if email notification is requested
      if (notificationData.sendEmail && (!notificationData.email || notificationData.email.trim() === '')) {
        setError('Email is required when sending email notification');
        return;
      }

      // Validate manually entered contact number if SMS notification is requested
      if (notificationData.sendSMS && (!notificationData.contactNumber || notificationData.contactNumber.trim() === '')) {
        setError('Contact number is required when sending SMS notification');
        return;
      }
    }
    // If using system contact, backend will validate and return proper error if missing

    try {
      setLoading(true);
      setError(null);

      // Prepare the request payload
      const requestPayload: any = {
        useSystemContact: notificationData.useSystemContact,
        sendEmail: notificationData.sendEmail,
        sendSMS: notificationData.sendSMS,
      };

      // Only include email/contactNumber if manually entered (not using system contact)
      // If using system contact, backend will fetch from database
      if (!notificationData.useSystemContact) {
        if (notificationData.sendEmail) {
          requestPayload.email = notificationData.email;
        }
        if (notificationData.sendSMS) {
          requestPayload.contactNumber = notificationData.contactNumber;
        }
      }

      console.log('Sending API request with payload:', requestPayload);
      console.log('Borrower ID:', selectedBorrowerForMenu.id);
      
      // Call the API
      const response = await sendBalanceNotification(selectedBorrowerForMenu.id, requestPayload);

      console.log('API Response:', response);

      // Handle response - check for both possible response structures
      if (response) {
        // Check if response has statusCode (from ApiResponse)
        if (response.statusCode === 200 || response.code === 200 || response.status === 200) {
          const successMessage = response.message || response.data || 'Notification sent successfully!';
          setApiMessage(successMessage);
          toast.success(successMessage, { position: 'top-right', autoClose: 3000 });
          handleCloseNotifyDialog();
        } else {
          const errorMsg = response.message || `Failed to send notification (Status: ${response.statusCode || response.code || 'Unknown'})`;
          setError(errorMsg);
          toast.error(errorMsg, { position: 'top-right', autoClose: 5000 });
        }
      } else {
        const errorMsg = 'No response received from server';
        setError(errorMsg);
        toast.error(errorMsg, { position: 'top-right', autoClose: 5000 });
      }
    } catch (err: any) {
      console.error('Error sending notification:', err);
      console.error('Error details:', {
        message: err?.message,
        response: err?.response,
        responseData: err?.response?.data,
        status: err?.response?.status,
      });
      const errorMessage = err?.response?.data?.message 
        || err?.message 
        || 'Failed to send notification. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage, { position: 'top-right', autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = apiBorrowers.filter(borrower => {
    const matchesSearch = borrower.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         borrower.comments.toLowerCase().includes(searchTerm.toLowerCase());
    // Map creditStatus to comparable status
    const statusMapping: { [key: string]: string } = {
      'GOOD': 'active',
      'BAD': 'inactive',
      'BLOCKED': 'blacklisted'
    };
    const borrowerStatus = statusMapping[borrower.creditStatus] || 'inactive';
    const matchesStatus = statusFilter === 'all' || borrowerStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getCustomerOutstanding = (borrowerId: string) => {
    const borrower = apiBorrowers.find(b => b.id === borrowerId);
    return borrower ? borrower.totalOutstanding : 0;
  };

  const getCustomerLoans = (customerId: string) => {
    return loans.filter(loan => loan.customerId === customerId);
  };

  const getStatusColor = (creditStatus: string) => {
    switch (creditStatus) {
      case 'GOOD': return 'success';
      case 'BAD': return 'error';
      case 'BLOCKED': return 'error';
      default: return 'default';
    }
  };

  const getCreditStatusLabel = (creditStatus: string) => {
    switch (creditStatus) {
      case 'GOOD': return 'Active';
      case 'BAD': return 'Inactive';
      case 'BLOCKED': return 'Blacklisted';
      default: return 'Unknown';
    }
  };

  const getChipColor = (creditStatus: string): 'success' | 'warning' | 'error' => {
    if (creditStatus === 'GOOD') return 'success';
    if (creditStatus === 'BAD') return 'warning';
    return 'error';
  };

  const getEmploymentStatusColor = (status: string) => {
    switch (status) {
      case 'employed': return 'success';
      case 'self-employed': return 'info';
      case 'unemployed': return 'warning';
      case 'retired': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: 2
      }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Customer Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => fetchBorrowers()}
            disabled={loading}
            size="small"
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAddDialog}
            size="small"
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {apiMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setApiMessage(null)}>
          {apiMessage}
        </Alert>
      )}

      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={mainTabValue} onChange={handleMainTabChange}>
          <Tab label="Customers" />
          <Tab label="Payment History" />
        </Tabs>
      </Box>

      {/* Tab Panel: Customers */}
      {mainTabValue === 0 && (
        <>
          {/* Search and Filter */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  fullWidth
                  label="Search customers"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status Filter"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="blacklisted">Blacklisted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={3}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  fullWidth
                  size="small"
                >
                  More Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Customer List */}
          <Paper>
            {!loading && filteredCustomers.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>No borrowers found</Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search criteria or use the refresh button to reload data
                </Typography>
              </Box>
            ) : (
              <>
                {/* Desktop Table View */}
                <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell align="right">Outstanding</TableCell>
                        <TableCell>Last Payment</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Credit Score</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCustomers.map((borrower) => (
                        <TableRow key={borrower.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 2 }}>
                                {borrower.name.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {borrower.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ID: {borrower.id.substring(0, 8)}...
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" color="error.main" fontWeight="bold">
                              {formatCurrency(getCustomerOutstanding(borrower.id))}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Outstanding
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {borrower.lastPaymentDate
                                ? new Date(borrower.lastPaymentDate).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                  })
                                : 'No payments'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Last payment
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getCreditStatusLabel(borrower.creditStatus)}
                              color={getStatusColor(borrower.creditStatus) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {borrower.creditScore !== undefined && borrower.creditScore !== null ? (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 1,
                                  borderRadius: 2,
                                  bgcolor:
                                    borrower.creditScore >= 4
                                      ? 'success.light'
                                      : borrower.creditScore >= 2
                                      ? 'warning.light'
                                      : 'error.light',
                                  color: 'common.white',
                                  minWidth: 90,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.3,
                                    color: 'inherit',
                                  }}
                                >
                                  Credit score
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.1,
                                    color: 'inherit',
                                  }}
                                >
                                  {borrower.creditScore}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewCustomer(borrower)}
                                title="View Customer Details"
                                color="primary"
                              >
                                <Visibility />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleViewDebits(borrower)}
                                title="View Debits"
                                color="info"
                              >
                                <AccountBalance />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="success"
                                onClick={() => handleOpenCaptureDebitDialog(borrower.id)}
                                title="Capture Debit"
                              >
                                <AttachMoney />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, borrower)}
                                title="More Options"
                                color="default"
                              >
                                <MoreVert />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Mobile Card View */}
                <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
                  {filteredCustomers.map((borrower) => (
                    <Card key={borrower.id} sx={{ mb: 2, p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                          <Avatar sx={{ width: 48, height: 48 }}>
                            {borrower.name.charAt(0)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight="bold" noWrap>
                              {borrower.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              ID: {borrower.id.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                          <Chip
                            label={getCreditStatusLabel(borrower.creditStatus)}
                            color={getStatusColor(borrower.creditStatus) as any}
                            size="small"
                          />
                          {borrower.creditScore !== undefined && borrower.creditScore !== null ? (
                            <Chip
                              label={`Score ${borrower.creditScore}`}
                              size="small"
                              color={
                                borrower.creditScore >= 4
                                  ? 'success'
                                  : borrower.creditScore >= 2
                                  ? 'warning'
                                  : 'error'
                              }
                              sx={{ fontWeight: 'bold' }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Score —
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Outstanding
                        </Typography>
                        <Typography variant="h6" color="error.main" fontWeight="bold">
                          {formatCurrency(getCustomerOutstanding(borrower.id))}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Last payment
                        </Typography>
                        <Typography variant="body2">
                          {borrower.lastPaymentDate
                            ? new Date(borrower.lastPaymentDate).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })
                            : 'No payments'}
                        </Typography>
                      </Box>

                      {borrower.comments && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Comments
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {borrower.comments}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => handleViewCustomer(borrower)}
                          sx={{ flex: 1, minWidth: '100px' }}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AccountBalance />}
                          onClick={() => handleViewDebits(borrower)}
                          sx={{ flex: 1, minWidth: '100px' }}
                          color="info"
                        >
                          Debits
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AttachMoney />}
                          onClick={() => handleOpenCaptureDebitDialog(borrower.id)}
                          sx={{ flex: 1, minWidth: '100px' }}
                          color="success"
                        >
                          Capture
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<MoreVert />}
                          onClick={(e) => handleMenuOpen(e, borrower)}
                          sx={{ flex: 1, minWidth: '100px' }}
                        >
                          More
                        </Button>
                      </Box>
                    </Card>
                  ))}
                </Box>
              </>
            )}
            {totalBorrowers > 0 && (
              <TablePagination
                component="div"
                count={totalBorrowers}
                page={customerPage}
                onPageChange={handleCustomerPageChange}
                rowsPerPage={customerRowsPerPage}
                onRowsPerPageChange={handleCustomerRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
            )}
          </Paper>
        </>
      )}

      {/* Tab Panel: Payment History */}
      {mainTabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Global Payment History</Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchAllPayments}
              disabled={loadingAllPayments}
            >
              Refresh
            </Button>
          </Box>

          {loadingAllPayments && <LinearProgress sx={{ mb: 2 }} />}

          {allPayments.length === 0 && !loadingAllPayments ? (
            <Alert severity="info">
              No payment records found.
            </Alert>
          ) : (
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Debit ID</TableCell>
                      <TableCell>Borrower</TableCell>
                      <TableCell>Received By</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allPayments.map((payment) => {
                      const borrower = apiBorrowers.find(b => 
                        debits.some(d => d.id === payment.debitId && d.borrowerId === b.id)
                      );
                      
                      return (
                        <TableRow key={payment.id || payment.paymentId} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {(payment.id || payment.paymentId || '').substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {payment.createdDate ? new Date(payment.createdDate).toLocaleDateString() : 
                             payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="success.main" fontWeight="bold">
                              {formatCurrency(payment.amount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={payment.paymentType || 'N/A'} 
                              size="small"
                              color={
                                payment.paymentType === 'REPAYMENT' ? 'success' : 
                                payment.paymentType === 'TOP_UP' ? 'info' : 
                                payment.paymentType === 'INTEREST' ? 'warning' :
                                'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {(payment.debitId || 'N/A').substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {borrower?.name || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>{payment.paymentReceivedBy || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {payment.note || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalPayments}
                page={paymentPage}
                onPageChange={handlePaymentPageChange}
                rowsPerPage={paymentRowsPerPage}
                onRowsPerPageChange={handlePaymentRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* Customer Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Customer Details
          {selectedCustomer && (
            <Typography variant="body2" color="text.secondary">
              {selectedCustomer.firstName} {selectedCustomer.lastName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ minWidth: { xs: '100%', sm: 600, md: 800 } }}>
          {selectedCustomer && (
            <Box>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Profile" />
                {/* <Tab label="Loans" /> */}
                <Tab label="Debits" />
                <Tab label="Payments" />
                {/* <Tab label="Documents" /> */}
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom>Personal Information</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Full Name</Typography>
                        <Typography variant="body1">
                          {selectedCustomer.firstName} {selectedCustomer.lastName}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Email</Typography>
                        <Typography variant="body1">{selectedCustomer.email}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Phone</Typography>
                        <Typography variant="body1">{selectedCustomer.phone}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                        <Typography variant="body1">
                          {selectedCustomer.dateOfBirth.toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom>Financial Information</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Credit Score</Typography>
                        <Typography variant="body1">
                          {selectedCustomer.creditScore || 'Not available'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Monthly Income</Typography>
                        <Typography variant="body1">
                          {formatCurrency(selectedCustomer.monthlyIncome)}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Employment Status</Typography>
                        <Chip
                          label={selectedCustomer.employmentStatus}
                          color={getEmploymentStatusColor(selectedCustomer.employmentStatus) as any}
                          size="small"
                        />
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>
{/* 
              <TabPanel value={tabValue} index={1}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>Active Loans</Typography>
                <Grid container spacing={2}>
                  {getCustomerLoans(selectedCustomer.id).map((loan) => (
                    <Grid item xs={12} md={6} lg={4} key={loan.id}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>{loan.loanNumber}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {loan.loanType} - {loan.purpose}
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Principal</Typography>
                              <Typography variant="body1">
                                {formatCurrency(loan.principalAmount)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Interest Rate</Typography>
                              <Typography variant="body1">
                                {loan.interestRate}%
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </TabPanel> */}

              <TabPanel value={tabValue} index={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Debits</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenAddDebitDialog}
                    size="small"
                  >
                    Add Debit
                  </Button>
                </Box>

                {loadingDebits ? (
                  <LinearProgress />
                ) : debits.length === 0 ? (
                  <Alert severity="info">
                    No debits found for this customer. Click "Add Debit" to create one.
                  </Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Amount</TableCell>
                          <TableCell>Outstanding</TableCell>
                          <TableCell>Interest Rate</TableCell>
                          <TableCell>Start Date</TableCell>
                          <TableCell>End Date</TableCell>
                          <TableCell>EMI Amount</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {debits.map((debit, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{formatCurrency(debit.amount || 0)}</TableCell>
                            <TableCell>{formatCurrency(debit.outstanding || 0)}</TableCell>
                            <TableCell>{debit.interestRate || 0}%</TableCell>
                            <TableCell>{debit.startDate || 'N/A'}</TableCell>
                            <TableCell>{debit.endDate || 'N/A'}</TableCell>
                            <TableCell>{formatCurrency(debit.emiAmount || 0)}</TableCell>
                            <TableCell>
                              <Chip 
                                label={debit.status || 'N/A'} 
                                color={debit.status === 'ACTIVE' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Typography variant="h6" gutterBottom>Payment History</Typography>
                <Alert severity="info">
                  Payment history will be displayed here
                </Alert>
              </TabPanel>

              {/* <TabPanel value={tabValue} index={4}>
                <Typography variant="h6" gutterBottom>Documents</Typography>
                <Alert severity="info">
                  Customer documents will be displayed here
                </Alert>
              </TabPanel> */}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button variant="contained">Edit Customer</Button>
        </DialogActions>
      </Dialog>

      {/* Add Borrower Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Borrower</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Basic Information */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">Basic Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Name *"
                      value={newBorrower.name}
                      onChange={(e) => setNewBorrower({ ...newBorrower, name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={newBorrower.email}
                      onChange={(e) => setNewBorrower({ ...newBorrower, email: e.target.value })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Comments"
                      value={newBorrower.comments}
                      onChange={(e) => setNewBorrower({ ...newBorrower, comments: e.target.value })}
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Contact Information */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">Contact Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      value={newBorrower.contactNumber}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewBorrower({ 
                          ...newBorrower, 
                          contactNumber: value,
                          whatsappNumber: newBorrower.isContactSameAsWhatsapp ? value : newBorrower.whatsappNumber
                        });
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="WhatsApp Number"
                      value={newBorrower.whatsappNumber}
                      onChange={(e) => setNewBorrower({ ...newBorrower, whatsappNumber: e.target.value })}
                      disabled={newBorrower.isContactSameAsWhatsapp}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WhatsApp />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newBorrower.isContactSameAsWhatsapp}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setNewBorrower({ 
                              ...newBorrower, 
                              isContactSameAsWhatsapp: checked,
                              whatsappNumber: checked ? newBorrower.contactNumber : newBorrower.whatsappNumber
                            });
                          }}
                        />
                      }
                      label="Contact Number same as WhatsApp Number"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Address Information */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">Address Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={newBorrower.address}
                      onChange={(e) => setNewBorrower({ ...newBorrower, address: e.target.value })}
                      multiline
                      rows={2}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOn />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Pincode"
                      value={newBorrower.pincode}
                      onChange={(e) => setNewBorrower({ ...newBorrower, pincode: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="City"
                      value={newBorrower.city}
                      onChange={(e) => setNewBorrower({ ...newBorrower, city: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="State"
                      value={newBorrower.state}
                      onChange={(e) => setNewBorrower({ ...newBorrower, state: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* CRM & Business Fields */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">CRM & Business Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Referral Code"
                      value={newBorrower.referralCode}
                      onChange={(e) => setNewBorrower({ ...newBorrower, referralCode: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Credit Score"
                      type="number"
                      value={newBorrower.creditScore}
                      onChange={(e) => setNewBorrower({ ...newBorrower, creditScore: e.target.value })}
                      inputProps={{ min: 0, max: 900 }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddBorrower}
            disabled={loading || !newBorrower.name.trim()}
          >
            Add Borrower
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Debit Dialog */}
      <Dialog
        open={openAddDebitDialog}
        onClose={handleCloseAddDebitDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Debit</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <AmountInput
                  fullWidth
                  label="Amount"
                  value={newDebit.amount}
                  onChange={(e) => setNewDebit({ ...newDebit, amount: e.target.value })}
                  placeholder="Enter amount"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AmountInput
                  fullWidth
                  label="Outstanding"
                  value={newDebit.outstanding}
                  onChange={(e) => setNewDebit({ ...newDebit, outstanding: e.target.value })}
                  placeholder="Enter outstanding amount"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Interest Rate (%)"
                  type="number"
                  value={newDebit.interestRate}
                  onChange={(e) => setNewDebit({ ...newDebit, interestRate: e.target.value })}
                  placeholder="e.g., 0.1"
                  inputProps={{ step: "0.01" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Duration Type</InputLabel>
                  <Select
                    value={newDebit.durationType}
                    label="Duration Type"
                    onChange={(e) => setNewDebit({ ...newDebit, durationType: e.target.value })}
                  >
                    <MenuItem value="DAILY">Daily</MenuItem>
                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                    <MenuItem value="YEARLY">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={newDebit.startDate}
                  onChange={(e) => setNewDebit({ ...newDebit, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={newDebit.endDate}
                  onChange={(e) => setNewDebit({ ...newDebit, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment Date (Day of Month)"
                  type="number"
                  value={newDebit.paymentDate}
                  onChange={(e) => setNewDebit({ ...newDebit, paymentDate: e.target.value })}
                  placeholder="e.g., 2"
                  inputProps={{ min: 1, max: 31 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AmountInput
                  fullWidth
                  label="EMI Amount"
                  value={newDebit.emiAmount}
                  onChange={(e) => setNewDebit({ ...newDebit, emiAmount: e.target.value })}
                  placeholder="Enter EMI amount"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="EMI Date"
                  type="date"
                  value={newDebit.emiDate}
                  onChange={(e) => setNewDebit({ ...newDebit, emiDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newDebit.status}
                    label="Status"
                    onChange={(e) => setNewDebit({ ...newDebit, status: e.target.value })}
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="CLOSED">Closed</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDebitDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddDebit}
            disabled={loading || !newDebit.amount || !newDebit.outstanding || !newDebit.startDate}
          >
            Add Debit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Capture Debit Modal */}
      {selectedBorrowerId && (
        <DebitCaptureModal
          open={openCaptureDebitDialog}
          borrowerId={selectedBorrowerId}
          handleDebitModelClose={handleCloseCaptureDebitDialog}
          onDebitCreated={handleDebitCreated}
        />
      )}

      {/* More Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleNotifyCustomer}>
          <ListItemIcon>
            <Notifications fontSize="small" />
          </ListItemIcon>
          <ListItemText>Notify Customer</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          setSelectedBorrowerForBrochure(selectedBorrowerForMenu);
          setOpenBrochureDialog(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <AccountBalance fontSize="small" />
          </ListItemIcon>
          <ListItemText>Generate Welcome Brochure</ListItemText>
        </MenuItem>
      </Menu>

      {/* Notify Customer Dialog */}
      <Dialog
        open={openNotifyDialog}
        onClose={handleCloseNotifyDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Notify Customer About Balance</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {selectedBorrowerForMenu && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {selectedBorrowerForMenu.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Outstanding: {formatCurrency(getCustomerOutstanding(selectedBorrowerForMenu.id))}
                </Typography>
              </Box>
            )}

            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">Contact Information</FormLabel>
              <RadioGroup
                value={notificationData.useSystemContact ? 'system' : 'manual'}
                onChange={(e) => setNotificationData({
                  ...notificationData,
                  useSystemContact: e.target.value === 'system',
                  email: e.target.value === 'system' && selectedBorrowerForMenu ? (selectedBorrowerForMenu.email || '') : notificationData.email,
                  contactNumber: e.target.value === 'system' && selectedBorrowerForMenu ? (selectedBorrowerForMenu.contactNumber || '') : notificationData.contactNumber,
                })}
              >
                <FormControlLabel
                  value="system"
                  control={<Radio />}
                  label={`Use system contact (Email: ${selectedBorrowerForMenu?.email || 'N/A'}, Phone: ${selectedBorrowerForMenu?.contactNumber || 'N/A'})`}
                />
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label="Enter manually"
                />
              </RadioGroup>
            </FormControl>

            {!notificationData.useSystemContact && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={notificationData.email}
                    onChange={(e) => setNotificationData({ ...notificationData, email: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    value={notificationData.contactNumber}
                    onChange={(e) => setNotificationData({ ...notificationData, contactNumber: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            )}

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Notification Method</FormLabel>
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationData.sendEmail}
                      onChange={(e) => setNotificationData({ ...notificationData, sendEmail: e.target.checked })}
                    />
                  }
                  label="Send Email"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationData.sendSMS}
                      onChange={(e) => setNotificationData({ ...notificationData, sendSMS: e.target.checked })}
                    />
                  }
                  label="Send SMS"
                />
              </Box>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotifyDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={(e) => {
              console.log('Send Notification button clicked');
              e.preventDefault();
              e.stopPropagation();
              handleSendNotification();
            }}
            disabled={loading || (!notificationData.sendEmail && !notificationData.sendSMS)}
          >
            {loading ? 'Sending...' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Welcome Brochure Viewer */}
      <BrochureViewer
        open={openBrochureDialog}
        onClose={() => {
          setOpenBrochureDialog(false);
          setSelectedBorrowerForBrochure(null);
        }}
        initialBorrowerId={selectedBorrowerForBrochure?.id}
      />
    </Box>
  );
};

export default CustomerManagement;
